import { Component, Input } from '@angular/core';
import { AmorphicService } from '../../../services/amorphic/amorphic.service';
import { AccountCenterService } from '../../../services/account-center/account-center.service';
import { Controller } from '../../../../../coverpath-client/public/js/Controller';
import { PaymentInformation } from '../../../../classes/PaymentInformation';
import { Product } from '../../../../../common/js/static/Product';
import { Utils } from '../../../../../common/js/Utils';
import { CoverpathUtils } from '../../../../js/static/CoverpathUtils';
import type { CoverpathBasePolicy } from '../../../../classes/coverpath_subclasses/CoverpathBasePolicy';
import type { CoverpathPolicy } from '../../../../classes/coverpath_subclasses/CoverpathPolicy';
import { PolicyRider } from '../../../../../common/js/customer/PolicyRider';
import { Exchange1035FundingPurpose } from '../../../../../coverpath-common/js/interfaces/CoverpathTypes';

@Component({
  moduleId: module.id,
  selector: 'payment-if-missing-policy-info',
  templateUrl: './policy-info.component.html',
})
export class PaymentIfMissingPolicyInfoComponent {
  @Input() instanceName: string;
  controller: Controller;
  submitDisabled: boolean = true;
  formattedActiveFace: string;
  formattedFace: string;
  paymentInformation: PaymentInformation;
  faceOverTlicFace: boolean;
  tlicCap: number;
  formattedTlicCap: string;
  formattedPremiumAmount: string;
  policyDescription: string;
  secondaryPolicyDescription: string;
  secondaryFormattedFace: string;
  paymentTerm: string;
  paymentTermAbbrv: string;
  accountEnding: string;
  formattedTlicAmount: string;
  existingAccountType: string;
  isJointApp: boolean;
  isMMD: boolean = false;
  tlicMode: boolean = false;
  totalFormattedFace: string;
  basePremium: number;
  policyRiders: PolicyRider[] = [];
  is1035ToPremium: boolean = false;
  hasRefund: boolean = false;
  formattedRefundAmount: string;

  constructor(private amorphicService: AmorphicService, private accountCenterService: AccountCenterService) {
    this.accountCenterService = accountCenterService;
    this.controller = this.amorphicService.controller as Controller;
    this.paymentInformation = this.controller.accountCenterController.paymentInformation;
  }

  getPolicyDescription(policy: CoverpathBasePolicy): string {
    return CoverpathUtils.getProductDescription(
      policy.productTypeWithPendingAmendment() === Product.productTypes.Term.code,
      policy.termWithPendingAmendment(),
      false,
      policy.product
    );
  }

  ngOnInit(): void {
    const policy = this.controller.customer.application.primaryPolicyWithPendingAmendment;
    this.isMMD = !!policy.isMMDirectPolicy();
    this.totalFormattedFace = Utils.formatIntCurrency(
      policy.application.getTotalFaceAmountWithPendingAmendment().toString()
    );
    this.formattedFace = Utils.formatIntCurrency(policy.faceAmountWithPendingAmendment().toString());
    this.tlicCap = policy.tLICFaceAmountCap;
    this.policyDescription = this.getPolicyDescription(policy);
    const premBd = policy.premiumBreakdown.getTotal();
    const tlicPremBd = !!policy.convertedFrom && policy.tlicAccepted() ? policy.tLICPremiumBreakdown.getTotal() : 0;
    const refundAmount = tlicPremBd - premBd;
    this.formattedRefundAmount = Utils.formatCurrency(refundAmount);
    this.hasRefund = refundAmount > 0;
    this.paymentTerm = policy.paymentFrequencyWithPendingAmendment().toLowerCase() === 'annual' ? 'Yearly' : 'Monthly';
    this.paymentTermAbbrv = policy.paymentFrequencyWithPendingAmendment().toLowerCase() === 'annual' ? 'yr' : 'mo';
    let secondaryPolicy;
    if (policy.application.isJointAppWithPendingAmendment()) {
      secondaryPolicy = this.controller.customer.application.secondaryPolicyWithPendingAmendment;
      this.isJointApp = true;
      this.secondaryPolicyDescription = this.getPolicyDescription(secondaryPolicy);
      this.secondaryFormattedFace = Utils.formatIntCurrency(
        secondaryPolicy.faceAmountWithPendingAmendment().toString()
      );
    }

    if (policy.paymentInformationGathered) {
      const account = policy.paymentAccount;
      this.accountEnding = account.substring(account.length - 4, account.length);
      this.existingAccountType = { '1': 'Savings', '2': 'Checking' }[policy.paymentAccountType];
    }

    this.tlicMode =
      this.instanceName === 'payment-tlic' ||
      this.instanceName === 'information-tlic' ||
      this.instanceName === 'confirm-tlic';

    if (this.tlicMode) {
      this.formattedTlicAmount = Utils.formatCurrency(policy.application.getTotalTlicPremium(), 2);
      this.formattedTlicCap = Utils.formatCurrency(this.tlicCap, 0);
      this.faceOverTlicFace = policy.application.getTotalFaceAmountWithPendingAmendment() > this.tlicCap;
      this.formattedActiveFace = this.faceOverTlicFace ? this.formattedTlicCap : this.totalFormattedFace;
    } else {
      const premium = policy.application.getTotalPremiumWithPendingAmendment();
      this.formattedPremiumAmount = Utils.formatCurrency(premium, 2);
      this.basePremium = policy.premiumBreakdown.baseWithPendingAmendment();
      this.formattedActiveFace = Utils.formatIntCurrency(policy.faceAmountWithPendingAmendment().toString());
      if (policy.isCoverpathPolicy()) {
        const cpPolicy = policy as CoverpathPolicy;
        this.policyRiders = cpPolicy.ridersWithPendingAmendment();
      }
    }
    this.is1035ToPremium =
      policy.alirRider &&
      policy.alirRider.hasAlir1035ExchangeWithPendingAmendment() &&
      policy.alirRider.alir1035ApplyTowardsWithPendingAmendment() === Exchange1035FundingPurpose.PREMIUM_AND_ALIR;
  }
}
